import { Helmet } from "react-helmet-async";

/**
 * Renders JSON-LD <script> tags via react-helmet-async.
 *
 * XSS hardening: schema input is serialized with "<", ">", "&" escaped to
 * \u003c / \u003e / \u0026 so that even if a Dutchie product name contained
 * "</script><script>alert(1)</script>" the string cannot break out of the
 * application/ld+json context. This is the pattern documented in the
 * react-helmet-async + Google JSON-LD guidance.
 *
 * NOTE on `dangerouslySetInnerHTML`: this is the CORRECT pattern for
 * <script type="application/ld+json"> via react-helmet-async. Using React
 * children inside <script> would HTML-escape the JSON ("&quot;" etc.),
 * producing invalid structured data. Static-analysis tools that flag
 * dangerouslySetInnerHTML wholesale don't understand this context.
 */

// Non-Schema.org fields that have caused Google Search Console errors
// ("Invalid object type for field <parent_node>" etc). We strip them at
// the serialisation boundary so no future generator can accidentally leak
// CMS / ORM internals into structured data.
const FORBIDDEN_FIELDS = new Set([
    "parent_node",
    "parentNode",
    "_parent",
    "cmsParent",
    "node",
    "internal",
    "payload",
    "raw",
    "databaseId",
    "__typename",
    "_id",
]);

// Strip forbidden keys recursively. Keeps every Schema.org-prefixed key
// (`@context`, `@type`, `@id`, `@graph`) and every dotted/camelCase
// Schema.org property name untouched.
function sanitizeSchema(value) {
    if (Array.isArray(value)) return value.map(sanitizeSchema);
    if (value && typeof value === "object") {
        const out = {};
        for (const [k, v] of Object.entries(value)) {
            if (FORBIDDEN_FIELDS.has(k)) continue;
            out[k] = sanitizeSchema(v);
        }
        return out;
    }
    return value;
}

function safeJsonLd(obj) {
    return JSON.stringify(sanitizeSchema(obj))
        .replace(/</g, "\\u003c")
        .replace(/>/g, "\\u003e")
        .replace(/&/g, "\\u0026");
}

export default function SchemaJsonLd({ schema, schemas }) {
    // Accept either `schema=` (singular legacy) or `schemas=` (plural). Some
    // pages historically called it with the plural — that used to silently
    // drop the entire array. Defensive accept-both prevents the regression.
    const input = schemas ?? schema;
    if (!input) return null;
    const list = Array.isArray(input) ? input : [input];
    return (
        <Helmet>
            {list.map((s, i) => {
                const id = s["@id"] || s["@type"] || `schema-${i}`;
                return (
                    <script
                        key={id}
                        type="application/ld+json"
                        dangerouslySetInnerHTML={{ __html: safeJsonLd(s) }}
                    />
                );
            })}
        </Helmet>
    );
}
